import type { FieldErrors } from "react-hook-form";
import { useForm } from "react-hook-form";
import TextInput from "../components/UI/TextInput";
import ToggleableButton from "../components/UI/ToggleagleButton";
import { useEffect, useState } from "react";
import ErrorMessage from "../components/UI/ErrorMessage";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Pod24CommonLayout } from "../layouts/Pod24CommonLayout";
import { Button } from "../components/UI/Button";
import { useAuthStore } from "../stores/auth-store";
import { useUserStore } from "../stores/user-store";
import { companyUseCase, userUseCase } from "../../di/container";
import { environment } from "../../infrastructure/config/environment";

export const CompanyApplication: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { fetchProfile, profile } = useUserStore();
  const [isFinished, setIsFinished] = useState(false);

  useEffect(() => {
    if (!(user?.role === "USER")) {
      navigate("/");
    }
  }, [navigate, user?.role]);

  useEffect(() => {
    (async () => {
      if (user?.id) {
        await fetchProfile(user?.id);
      }
    })();
  }, [fetchProfile, user?.id]);

  useEffect(() => {
    if (profile && profile.id === user?.id && profile?.isRequestCompany) {
      setIsFinished(true);
    }
  }, [profile, user?.id]);

  return (
    <>
      <Pod24CommonLayout>
        {isFinished ? (
          <>
            <ApplicationCompleteView />
          </>
        ) : (
          <FormLayout setIsFinished={setIsFinished} />
        )}
      </Pod24CommonLayout>
    </>
  );
};

/** 사업자등록번호 검증번호 확인 (국세청 공개 산식). 서버도 같은 검사를 한다 */
const isValidBusinessNumber = (value: string) => {
  const n = value.replace(/\D/g, "");
  if (!/^\d{10}$/.test(n)) return false;
  const d = n.split("").map(Number);
  const w = [1, 3, 7, 1, 3, 7, 1, 3, 5];
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += d[i] * w[i];
  sum += Math.floor((d[8] * 5) / 10);
  return (10 - (sum % 10)) % 10 === d[9];
};

interface ApplicationFormData {
  companyName: string;
  ceoName: string;
  businessNumber: string;
  businessType: string;
  businessCategory: string;
  zipCode: string;
  address: string;
  addressDetail: string;
  phone: string;
}

/**
 * 업체 신청서.
 *
 * 업체명 외 항목은 세금계산서·현금영수증 발급(국세청 인증 연동사 회원가입)에 필수다.
 * 사업자등록증에 적힌 그대로 받아 두고, 승인되면 내 회사 정보로 옮겨진다.
 */
export const ApplicationForm: React.FC<{
  setPageName: React.Dispatch<React.SetStateAction<string>>;
  setIsFinished: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ setPageName, setIsFinished }) => {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 페이지 이름 변경
  useEffect(() => {
    setPageName("업체 신청하기");
  }, [setPageName]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ApplicationFormData>({ shouldUnregister: false });

  // 주소 검색 팝업(행정안전부 도로명주소)이 백엔드 콜백을 거쳐 postMessage 로 결과를 보낸다. 내 회사 정보와 같은 방식
  useEffect(() => {
    const handleMessage = (event: MessageEvent<any>) => {
      const data = event.data;
      if (!data || typeof data !== "object") return;
      if (data["zipNo"]) setValue("zipCode", data["zipNo"]);
      const road = [data["roadAddrPart1"], data["roadAddrPart2"]].filter(Boolean).join(" ");
      if (road) setValue("address", road, { shouldValidate: true });
      if (data["addrDetail"]) setValue("addressDetail", data["addrDetail"]);
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [setValue]);

  const handleAddressSearch = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const returnUrl = `${environment.apiBaseUrl}/api/address`;
    const url = `https://business.juso.go.kr/addrlink/addrLinkUrl.do?confmKey=${process.env.REACT_APP_JUSO_CONFM_KEY}&returnUrl=${returnUrl}&resultType=1&useDetailAddr=Y&resultType=4`;
    window.open(url, "jusoPopup", "width=570,height=420,scrollbars=yes,resizable=yes");
  };

  // 양식 제출시 실행되는 함수.
  const onSubmit = async (data: ApplicationFormData) => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const response = await companyUseCase.requestCompany({
        companyName: data.companyName.trim(),
        ceoName: data.ceoName.trim(),
        businessNumber: data.businessNumber.replace(/\D/g, ""),
        businessType: data.businessType.trim(),
        businessCategory: data.businessCategory.trim(),
        zipCode: data.zipCode.trim() || null,
        address: data.address.trim(),
        addressDetail: data.addressDetail.trim() || null,
        phone: data.phone.replace(/\D/g, ""),
      });
      if (response) setIsFinished(true);
    } catch (error) {
      const original = (error as { originalError?: unknown })?.originalError;
      const message = original instanceof Error ? original.message : error instanceof Error ? error.message : null;
      setSubmitError(message?.replace(/^Error:\s*/, "") || "업체 신청에 실패했습니다. 잠시 후 다시 시도해 주세요.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const onError = (_: FieldErrors<ApplicationFormData>) => {
    setSubmitError(null);
  };

  const fieldError = (key: keyof ApplicationFormData) =>
    errors[key]?.message
      ? { message: String(errors[key]?.message), isIconVisible: true, isMessageVisible: true }
      : undefined;

  const required = (label: string) => ({ required: { value: true, message: `${label}을(를) 입력해 주세요.` } });

  return (
    <>
      <h3 className="w-full text-left font-medium mb-2">신청할 업체 정보를 입력해 주세요.</h3>
      <p className="text-[13px] text-[#888] mb-6">
        사업자등록증에 적힌 그대로 입력해 주세요. 승인되면 내 회사 정보로 옮겨지고, 세금계산서·현금영수증 발급에 그대로 쓰입니다.
      </p>
      {submitError && (
        <div className="mb-5 rounded-[8px] border border-[#F24A4A]/20 bg-[#FEF2F2] px-4 py-3 text-[13px] text-[#F24A4A]">
          {submitError}
        </div>
      )}
      <form onSubmit={handleSubmit(onSubmit, onError)} className="flex flex-col gap-5" noValidate>
        <Field label="상호(업체명)" required>
          <TextInput {...register("companyName", required("상호(업체명)"))} error={fieldError("companyName")} placeholder="예) 데이토즈인쇄" maxLength={100} />
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="대표자명" required>
            <TextInput {...register("ceoName", required("대표자명"))} error={fieldError("ceoName")} maxLength={50} />
          </Field>
          <Field label="사업자등록번호" required>
            <TextInput
              {...register("businessNumber", {
                ...required("사업자등록번호"),
                validate: (v) => isValidBusinessNumber(v) || "사업자등록번호를 정확히 입력해 주세요.",
              })}
              error={fieldError("businessNumber")}
              inputMode="numeric"
              placeholder="- 없이 10자리"
              maxLength={12}
            />
          </Field>
          <Field label="업태" required>
            <TextInput {...register("businessType", required("업태"))} error={fieldError("businessType")} placeholder="예) 제조업" maxLength={100} />
          </Field>
          <Field label="종목" required>
            <TextInput {...register("businessCategory", required("종목"))} error={fieldError("businessCategory")} placeholder="예) 인쇄업" maxLength={100} />
          </Field>
        </div>

        <Field label="사업장 주소" required>
          <div className="flex gap-2">
            <TextInput {...register("zipCode")} readOnly placeholder="우편번호" className="w-[140px]" />
            <button
              type="button"
              onClick={handleAddressSearch}
              className="shrink-0 px-4 h-12 rounded-[8px] border border-[#222] text-[14px] hover:bg-[#f5f5f5]"
            >
              주소 검색
            </button>
          </div>
          <TextInput
            {...register("address", required("사업장 주소"))}
            error={fieldError("address")}
            placeholder="주소 검색으로 입력하거나 직접 입력해 주세요"
            maxLength={300}
            className="mt-2"
          />
          <TextInput {...register("addressDetail")} placeholder="상세주소 (선택)" maxLength={200} className="mt-2" />
        </Field>

        <Field label="대표 전화번호" required>
          <TextInput
            {...register("phone", {
              ...required("대표 전화번호"),
              validate: (v) => /^\d{8,11}$/.test(v.replace(/\D/g, "")) || "전화번호를 정확히 입력해 주세요.",
            })}
            error={fieldError("phone")}
            inputMode="tel"
            placeholder="예) 02-1234-5678"
            maxLength={13}
          />
        </Field>

        <div className="w-full flex flex-row justify-end">
          <ToggleableButton className="w-[200px]" isEnabled={!isSubmitting}>
            {isSubmitting ? "신청 중..." : "신청하기"}
          </ToggleableButton>
        </div>
      </form>
    </>
  );
};

const Field: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({ label, required, children }) => (
  <div className="flex flex-col gap-2">
    <span className="text-[14px] font-medium text-[#555]">
      {label}
      {required && <span className="text-[#4285F4] ml-0.5">*</span>}
    </span>
    {children}
  </div>
);

export const FormLayout: React.FC<{
  setIsFinished: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ setIsFinished }) => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const [pageName, setPageName] = useState("업체 신청하기"); // 페이지 이름

  // 버튼 이벤트 핸들러
  const handleBackClick = () => {
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* 메인 콘텐츠 */}
      <div className="flex flex-col items-center / mt-10">
        <div className="w-full max-w-[750px] / px-5">
          <div className="flex flex-row items-center gap-5 / w-[100%] / mb-6">
            <div role="button" onClick={handleBackClick}>
              <svg
                width="8"
                height="12"
                viewBox="0 0 8 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.12095 0.170067C7.32709 0.376209 7.34583 0.698787 7.17717 0.926099L7.12095 0.991223L2.11243 6L7.12095 11.0088C7.32709 11.2149 7.34583 11.5375 7.17717 11.7648L7.12095 11.8299C6.91481 12.0361 6.59223 12.0548 6.36492 11.8862L6.29979 11.8299L0.880438 6.41058C0.674296 6.20444 0.655556 5.88186 0.824217 5.65455L0.880438 5.58942L6.29979 0.170067C6.52655 -0.056689 6.89419 -0.056689 7.12095 0.170067Z"
                  fill="#252525"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold">{pageName}</h2>
          </div>
          <div className="w-full border my-[30px] border-[#aeaeae]" />

          <ApplicationForm
            setIsFinished={setIsFinished}
            setPageName={setPageName}
          />
        </div>
      </div>
    </div>
  );
};

export const ApplicationCompleteView: React.FC = () => {
  const { user } = useAuthStore();

  const [displayingName, setDisplayingName] = useState("OOO");
  const navigate = useNavigate();
  const handleLater = () => {
    navigate("/");
  };

  const handleEmailVerification = () => {
    // TODO: 이메일 인증 로직 구현
    console.log("이메일 인증 처리");
    // 임시로 홈으로 이동
    navigate("/");
  };

  return (
    <div className="flex justify-center px-4 pt-[10rem] pb-[10rem]">
      <div className="w-full max-w-md text-center">
        {/* 환영 메시지 */}
        <div className="mb-[5rem]">
          <h1
            className="font-['Pretendard'] font-semibold text-gray-900 mb-6"
            style={{ fontSize: "24px" }}
          >
            신청이 완료되었습니다
          </h1>
          <p className="mb-4" style={{ fontSize: "16px" }}>
            <span className="font-['Pretendard'] font-bold text-blue-600">
              {user?.name}
            </span>
            <span className="font-['Pretendard'] font-semibold text-gray-900">
              님 업체 등록 신청이 완료되었습니다!
            </span>
          </p>
          <div
            className="font-['Pretendard'] font-normal text-gray-500"
            style={{ fontSize: "14px", height: "36px", marginTop: "16px" }}
          >
            <p>신청 이후 승인까지는 1~2일 정도가 소요됩니다.</p>
            <p>승인되지 않을 경우 admin@datoz.co.kr로 문의해보세요.</p>
          </div>
        </div>

        {/* 버튼들 */}
        <div className="w-full flex justify-stretch">
          <button
            onClick={handleLater}
            className="w-full h-[3rem] rounded-[8px] text-[1rem] border-2 border-blue-500 text-blue-500  font-semibold transition-colors hover:bg-blue-50"
          >
            이전 페이지로 돌아가기
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompanyApplication;
